import { createStyles, Link, Paper, Typography } from '@material-ui/core';
import { debounce } from 'lodash-es';
import React from 'react';

import { setToken } from '~/src/dash/api/auth/auth';
import { getOAuthToken } from '~/src/dash/api/console-api';
import { AsyncButtonV2 } from '~/src/dash/components/button';
import { DebugTopbar } from '~/src/dash/components/debug-topbar';
import {
  FormItemTypePassword,
  FormItemTypeText,
  IFormData,
  IFormItem,
  RenderForm,
} from '~/src/dash/components/form';
import { REGEX_EMAIL } from '~/src/dash/globals/constants';
import { isLoginDisabled } from '~/src/dash/globals/disable-login';
import { IStore } from '~/src/dash/reducers/store';
import {
  getApiDomain,
  getColorTheme,
  getDemoSSAIUsername,
  getEnv,
  getResellerID,
  getRoundLogoURL,
} from '~/src/dash/resellers/utils/importResellerConfig';
import { reload, RouteNetworkDashboard, RouteSession } from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { BaseClass } from '~/src/dash/views/base-view';
import { LoadingView } from '~/src/dash/views/loading-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  updateConsoleDown: VoidFunction;
}

interface IState {
  data: IFormData;
  showLoadingWheel: boolean;
  countSignin: number;
  signinFailed: boolean;
  pageLoading: boolean;
}

export const SigninView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4, // ?
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '300px',
          [theme.breakpoints.up('md')]: {
            maxWidth: '400px',
            marginTop: theme.spacing.unit * 6, // Margin above paper
          },
        },
        signHeader: {
          marginTop: theme.spacing.unit * 4, // ?
          textAlign: 'center',
          [theme.breakpoints.up('md')]: {
            marginTop: 0, // Margin above root component
          },
        },
        signHeaderLogo: {
          marginTop: theme.spacing.unit * 8, // Margin above logo
          display: 'inline-block',
          width: '150px',
          height: '65px',
          background: 'url(' + getRoundLogoURL() + ') no-repeat center',
          backgroundSize: 'contain',
        },
        signHeaderText: {
          color: theme.palette.secondary.contrastText,
          fontSize: theme.spacing.unit * 4,
          margin: theme.spacing.unit * 3,
        },
        passwordRecover: {
          textAlign: 'center',
          fontSize: theme.spacing.unit * 2,
          color: theme.palette.grey['800'],
          marginTop: theme.spacing.unit * 4,
          marginBottom: theme.spacing.unit * 4,
        },
        signupLink: {
          textAlign: 'center',
          fontSize: theme.spacing.unit * 2,
          color: theme.palette.grey['800'],
          marginTop: theme.spacing.unit * 2,
          '&>span': {
            display: 'block',
          },
          [theme.breakpoints.up('md')]: {
            marginTop: theme.spacing.unit * 4,
            marginBottom: theme.spacing.unit * 2,
            '&>span': {
              display: 'inline',
            },
          },
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      data: {} as IFormData,
      showLoadingWheel: false,
      countSignin: 0,
      signinFailed: false,
      pageLoading: true,
    };

    signinForm: IFormItem[] = [
      {
        id: 'Email',
        label: 'Email',
        required: true,
        render: FormItemTypeText,
        validate(_fi: IFormItem, _oldData: IFormData, _newData: IFormData) {
          return REGEX_EMAIL.test(_newData.Email || '') ? '' : 'Please enter valid email.';
        },
        props: {
          disabled: isLoginDisabled,
        },
      },
      {
        id: 'Password',
        label: 'Password',
        required: true,
        render: FormItemTypePassword,
        onKeyPress: (ev: React.KeyboardEvent) => {
          if (ev && ev.key === 'Enter') {
            this.login();
          }
        },
        props: {
          disabled: isLoginDisabled,
        },
      },
    ];

    _login = debounce((countSignin: number) => {
      if (countSignin !== this.state.countSignin) {
        return;
      }
      getOAuthToken({
        body: {
          grant_type: 'password',
          username: this.state.data.Email,
          password: this.state.data.Password,
        },
      }).then(
        () => {
          if (countSignin !== this.state.countSignin) {
            return;
          }
          reload(
            getDemoSSAIUsername() === this.state.data.Email.toLowerCase()
              ? RouteNetworkDashboard
              : RouteSession,
          );
          this.setState({
            showLoadingWheel: false,
            signinFailed: false,
          });
        },
        (reason) => {
          if (reason.error === 'Direct Login not allowed') {
            window.location.href = '/#/signin/S12';
            this.setState({
              showLoadingWheel: false,
            });
            return;
          }
          fetch(getApiDomain() + '/versionz').then(
            () => {
              if (countSignin !== this.state.countSignin) {
                return;
              }
              this.setState({
                showLoadingWheel: false,
                signinFailed: true,
              });
            },
            () => {
              fetch(getApiDomain() + '/versionz').then(() => {
                if (countSignin !== this.state.countSignin) {
                  return;
                }
                this.setState({
                  showLoadingWheel: false,
                  signinFailed: true,
                });
              }, this.props.updateConsoleDown);
            },
          );
        },
      );
    }, 200);

    constructor(props: IProps) {
      super(props);
      setToken('');
    }

    componentDidMount() {
      fetch(getApiDomain() + '/versionz').then(
        () => {
          this.setState({
            pageLoading: false,
          });
        },
        () => {
          fetch(getApiDomain() + '/versionz').then(() => {
            this.setState({
              pageLoading: false,
            });
          }, this.props.updateConsoleDown);
        },
      );
    }

    login = () => {
      if (isLoginDisabled) {
        return;
      }

      const countSignin = this.state.countSignin + 1;
      this.setState({
        showLoadingWheel: true,
        signinFailed: false,
        countSignin,
      });
      this._login(countSignin);
    };

    onUpdate = (data: IFormData) => {
      this.setState({ data });
    };

    handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      this.login();
    };

    renderErrors = () => {
      const path = window.location.href;
      const errorCode = path.split('signin/')[1];
      const resellerId = getResellerID();
      let errorMessage;
      switch (errorCode) {
        case 'S01':
          errorMessage =
            'S01: This publisher cannot be found. Please contact your administrator for further assistance.';
          break;
        case 'S02':
          errorMessage =
            'S02: Single Sign-On has not been enabled for your account. Please sign in with your username and password or contact your administrator for further assistance.';
          break;
        case 'S03':
          errorMessage =
            'S03: Unable to verify Single Sign-On configuration. Please contact your administrator for further assistance.';
          break;
        case 'S04':
          errorMessage =
            'S04: Unable to verify Single Sign-On configuration. Please contact your administrator for further assistance.';
          break;
        case 'S05':
          errorMessage =
            'S05: User account has not been created. Please contact your administrator to create an account.';
          break;
        case 'S06':
          errorMessage =
            resellerId <= 0 ? (
              <div>
                S06: An error has occurred. Please contact your administrator or{' '}
                <a href='mailto:contact@getpublica.com'>contact@getpublica.com</a> for further
                assistance.
              </div>
            ) : (
              'S06: An error has occurred. Please contact your administrator for further assistance.'
            );
          break;
        case 'S07':
          errorMessage =
            resellerId <= 0 ? (
              <div>
                S07: User account has not been created. Please contact your administrator or{' '}
                <a href='mailto:contact@getpublica.com'>contact@getpublica.com</a> for further
                assistance.
              </div>
            ) : (
              'S07: User account has not been created. Please contact your administrator for further assistance.'
            );
          break;
        case 'S08':
          errorMessage =
            resellerId <= 0 ? (
              <div>
                S08: User account has not been created. Please contact your administrator or{' '}
                <a href='mailto:contact@getpublica.com'>contact@getpublica.com</a> for further
                assistance.
              </div>
            ) : (
              'S08: User account has not been created. Please contact your administrator for further assistance.'
            );
          break;
        case 'S09':
          errorMessage =
            resellerId <= 0 ? (
              <div>
                S09: This user account cannot be found. Please contact your administrator or{' '}
                <a href='mailto:contact@getpublica.com'>contact@getpublica.com</a> for further
                assistance.
              </div>
            ) : (
              'S09: This user account cannot be found. Please contact your administrator for further assistance.'
            );
          break;
        case 'S10':
          errorMessage =
            resellerId <= 0 ? (
              <div>
                S10: An error has occurred. Please contact your administrator or{' '}
                <a href='mailto:contact@getpublica.com'>contact@getpublica.com</a> for further
                assistance.
              </div>
            ) : (
              'S10: An error has occurred. Please contact your administrator for further assistance.'
            );
          break;
        case 'S11':
          errorMessage =
            resellerId <= 0 ? (
              <div>
                S11: An error has occurred. Please contact your administrator or{' '}
                <a href='mailto:contact@getpublica.com'>contact@getpublica.com</a> for further
                assistance.
              </div>
            ) : (
              'S11: An error has occurred. Please contact your administrator for further assistance.'
            );
          break;
        case 'S12':
          errorMessage =
            "S12: This publisher has disabled Direct Login. Please sign in via your company's Single Sign-On app or contact your administrator for further assistance.";
          break;
        case 'S13':
          errorMessage =
            'S13: This SAML integration cannot be found. Please contact your administrator for further assistance.';
          break;
        default:
          return;
      }

      return <div style={{ color: 'red' }}>{errorMessage}</div>;
    };

    render = () => {
      const { classes } = this.props;
      if (this.state.pageLoading) {
        return <LoadingView active={true} text='Loading' />;
      }

      return (
        <div
          style={{
            width: '100%',
            height: '100vh',
            backgroundColor: getColorTheme('signInBackground'),
            margin: '0px 0px 0px 0px',
          }}
        >
          {getEnv() === 'staging' && <DebugTopbar />}
          <BaseClass id='signin'>
            <div className={classes.signHeader}>
              <div className={classes.signHeaderLogo} />
              <h3 className={classes.signHeaderText}>Hey, welcome back!</h3>
            </div>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              {this.renderErrors()}
              <form id='signin-form-body' onSubmit={this.handleSubmit}>
                <RenderForm
                  schema={this.signinForm}
                  data={this.state.data}
                  onUpdate={this.onUpdate}
                />
                <div>
                  {this.state.signinFailed && (
                    <Typography component='p' className='error-message'>
                      The email and password you entered don&apos;t match.
                    </Typography>
                  )}
                </div>
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <div>
                    {isLoginDisabled && (
                      <Typography
                        component='p'
                        className='error-message'
                        style={{ marginBottom: '1rem' }}
                      >
                        Sign-in is temporarily disabled.
                      </Typography>
                    )}
                  </div>

                  <AsyncButtonV2
                    text='Sign in'
                    inprogress={this.state.showLoadingWheel}
                    props={{
                      size: 'large',
                    }}
                    onClick={this.login}
                    disabled={isLoginDisabled}
                  />
                </div>
                {!isLoginDisabled && (
                  <Typography className={classes.passwordRecover}>
                    Forgot your password? <Link href='#/forgot-password'>Recover</Link>
                  </Typography>
                )}
                {/* <Divider style={{ marginTop: '1rem' }} /> */}
                {/* Signup path disabled HB - 1983 */}
                {/* <Typography component='p' className={classes.signupLink}>
                <span>Don&#39;t have an account? </span><Link href='#/signup'>Get Started</Link>
              </Typography> */}
              </form>
            </Paper>
          </BaseClass>
          {/* #HOLIDAYTHEME - DELETE BELOW AFTER HOLIDAYS */}
          <div className='snowflakes' aria-hidden='true'>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
            <div className='snowflake-css'>
              <div className='inner'>❅</div>
            </div>
          </div>
          {/* #HOLIDAYTHEME - DELETE ABOVE AFTER HOLIDAYS */}
        </div>
      );
    };
  },
);
